import {
  type FetchResult,
  type MutationOptions,
  type DefaultContext,
  type OperationVariables,
  type WatchQueryOptions
} from '@apollo/client/core/index.js';
import { readable, type Readable } from 'svelte/store';
import { browser } from '$app/environment';
import type { DataWrapper, QueryValue } from '$lib/types';
import { getApolloClient } from './core';

// Local wrapper of Apollo query so we can convert the ObservableQuery into a
// Svelte readable store and also handle SSR/Client hydration decently.
export function query<T = any>(
  options: WatchQueryOptions<OperationVariables, DataWrapper<T> | null> & {
    data?: DataWrapper<T>;
    unless?: boolean;
  }
): Readable<QueryValue<T>> {
  // Handle conditional query running
  if (options.unless) {
    return readable<QueryValue<T>>({ data: {}, loading: true }, () => {
      /* noop */
    });
  }

  // Always just write to the cache if the data is provided
  if (options.data) {
    getApolloClient().writeQuery({ ...options, data: options.data });
  }

  // If we're in the server we don't want to execute a query that might not resolve
  // before rendering completes as this is not an async/awaitable function. Instead,
  // attempt to read from the cache (Assuming a page data-loader has primed the cache)
  // and return that data instantly.
  if (!browser) {
    let data = getApolloClient().readQuery({ ...options });
    return readable<QueryValue<T>>({ data: data ?? {}, loading: !data }, () => {
      /* noop */
    });
  }

  // Watch the query and subscribe to the result in a readable svelte store
  const observable = getApolloClient().watchQuery({ ...options });
  const initialCacheRead = observable.getCurrentResult();
  return readable<QueryValue<T>>({ ...initialCacheRead, query: observable }, set => {
    const subscription = observable.subscribe(value => {
      set({ ...value, query: observable });
    });
    return () => subscription.unsubscribe();
  });
}

// Local wrapper of Apollo mutation
export function mutate<T = any>(
  options: MutationOptions<T, OperationVariables, DefaultContext>
): Promise<FetchResult<T>> {
  return getApolloClient().mutate({ ...options });
}
